import React, { useCallback, useMemo } from 'react'
import { FormTextField, FormToggle, useZionFormContext } from '@fs/zion-form'
import { Cell, FlowGrid, LabelSpacer, Paragraph, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  useSlsHighlightsContext,
  useSlsStateContext,
  getDisplayLabelForFieldType,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import { useFeatureFlag } from '@fs/zion-flags'
import FormStandardizedSexField from '../../../standardizedSexField/StandardizedSexField'
import { handleInputFocus, handleInputBlur } from '../../shared/utils/highlightUtils'
import HighlightButton from '../../panels/shared/HighlightButton'
import { useSexFieldValidation } from '../hooks/useSexFieldValidation'

/**
 * PersonalDetailsForm component renders a form section for entering personal details such as sex, race, and age.
 * It integrates with form, sls, and highlight contexts to manage form state and highlight interactions.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.person - The person object containing personal details to pre-populate the form
 * @param {boolean} props.forcePrimaryPerson - Whether the toggle should be disabled (true for CENSUS records)
 * @param {string} props.splitScreenImageArk - The ARK of the image in the split screen, used for highlights
 * @param {string} props.personId - The ID of the person being edited, used for highlights
 * @returns {JSX.Element} The rendered form section for personal details
 */
const PersonalDetailsForm = ({ person, forcePrimaryPerson, splitScreenImageArk, personId }) => {
  const [t] = useTranslation()
  const {
    formState: { defaultValues },
    getValues,
    resetField,
    setValue,
  } = useZionFormContext()
  const h = useSlsHighlightsContext()
  const { types: slsTypes, images, currentImageArk } = useSlsStateContext()
  const isNewPerson = defaultValues?.added ?? true
  const rip_sharedImageViewer_fullTextAttachFlow_v2 = useFeatureFlag('rip_sharedImageViewer_fullTextAttachFlow_v2')

  // Extract default field values for sex to determine if the field was pre-populated
  const defaultSexField = useMemo(() => defaultValues?.SEX_CODE?.value, [defaultValues])

  // Handlers to manage highlight hover on input focus/blur - using utility functions
  const onInputFocus = useCallback((fieldIdPath) => handleInputFocus(fieldIdPath, getValues, h), [getValues, h])

  const onInputBlur = useCallback(() => handleInputBlur(h), [h])

  const handleHighlightSave = useCallback(
    (fieldName) => (data) => {
      if (data?.newFieldId && data?.newFieldValue) {
        const currentFieldData = getValues(fieldName)

        resetField(fieldName, {
          defaultValue: {
            ...currentFieldData,
            id: data.newFieldId,
            value: data.newFieldValue,
            removed: false,
            dispatches: data?.dispatches || [],
            locked: data?.selectedPersonIds?.length > 1, // Lock the field if it's associated with multiple persons
          },
          keepDirty: false,
          keepErrors: false,
          keepTouched: false,
        })

        setValue(`${fieldName}.value`, data?.newFieldValue)
      }
    },
    [resetField, getValues, setValue]
  )

  // Prepare field object for HighlightButton component
  const prepareFieldForHighlights = useCallback(
    (type) => {
      const currentField = getValues(type)
      const existingSlsField = getNestedOrFloatingElementById(images, currentField?.id)
      const effectivePersonId = personId || existingSlsField?.personId

      return {
        ...currentField,
        displayLabel: getDisplayLabelForFieldType(type, slsTypes),
        name: `${type}.value`,
        fieldType: type,
        elementType: 'FIELD',
        personId: personId || existingSlsField?.personId, // Use personId from props or fallback to SLS data if available
        superElements: [
          {
            partitionKey: currentImageArk,
            id: effectivePersonId,
          },
        ],
        ...existingSlsField, // Spread existing SLS data
      }
    },
    [getValues, images, slsTypes, currentImageArk, personId]
  )

  const validationProps = useSexFieldValidation({ fieldName: 'SEX_CODE.value' })

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
      <Paragraph size="sm">
        <strong>{t('essentialInformationForm.personalDetails.title', 'Personal Details')}</strong>
      </Paragraph>

      <FormToggle
        name="primaryPerson"
        label={t('essentialInformationForm.primaryPerson', 'Is primary person for this record')}
        data-testid="primary-person-toggle"
        disabled={forcePrimaryPerson}
        title={
          forcePrimaryPerson
            ? t('essentialInformationForm.primaryPerson.disabledTitle', 'All persons on a census are primary')
            : ''
        }
      />

      <Row gutters="xxs">
        {/* Adding this check to prevent errors in case person data is not available, as the standardized sex field
        relies on person data to function properly. This input will throw an error if the user changes images with the form in edit mode
        because the person will become undefined */}
        {person && (
          <FormStandardizedSexField
            fieldName="SEX_CODE.value"
            addedSexCode={!defaultSexField ? { id: 'SEX_CODE' } : null}
            person={person}
            autosuggestProps={{
              dense: true,
              label: getDisplayLabelForFieldType('SEX_CODE', slsTypes),
              onFocus: () => onInputFocus('SEX_CODE.id'),
              onBlur: onInputBlur,
              'data-testid': 'sex-code-field',
              clearOnClickAway: false, // Allow validation to handle invalid values instead of clearing on click-away (outside click)
              ignoreTouchedForWarnings: true,
              ...validationProps, // Spread validation props to show warning or success messages based on the selected value
            }}
            helpMessage={
              rip_sharedImageViewer_fullTextAttachFlow_v2.isOn &&
              getValues('SEX_CODE.locked') &&
              'This field is associated with multiple names.'
            }
            growX
          />
        )}
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights('SEX_CODE')}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{
              'data-testid': 'sex-code-highlight-button',
            }}
            linkName="essential-information-sex-code-highlight"
            onSave={handleHighlightSave('SEX_CODE')}
            saveToSls={!isNewPerson && !getValues('SEX_CODE.added')} // Only save to SLS if this is not a new person and the field isn't newly added
            growX
            iconOnly
          />
        </Cell>
      </Row>

      <Row gutters="xxs">
        <FormTextField
          name="RACE_OR_COLOR.value"
          label={getDisplayLabelForFieldType('RACE_OR_COLOR', slsTypes)}
          onFocus={() => onInputFocus('RACE_OR_COLOR.id')}
          onBlur={onInputBlur}
          data-testid="race-or-color-field"
          helpMessage={
            rip_sharedImageViewer_fullTextAttachFlow_v2.isOn &&
            getValues('RACE_OR_COLOR.locked') &&
            'This field is associated with multiple names.'
          }
          dense
          growX
        />
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights('RACE_OR_COLOR')}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{
              'data-testid': 'race-or-color-highlight-button',
            }}
            onSave={handleHighlightSave('RACE_OR_COLOR')}
            saveToSls={!isNewPerson && !getValues('RACE_OR_COLOR.added')} // Only save to SLS if this is not a new person and the field isn't newly added
            growX
            iconOnly
          />
        </Cell>
      </Row>

      <Row gutters="xxs">
        <FormTextField
          name="AGE.value"
          label={getDisplayLabelForFieldType('AGE', slsTypes)}
          onFocus={() => onInputFocus('AGE.id')}
          onBlur={onInputBlur}
          data-testid="age-field"
          helpMessage={
            rip_sharedImageViewer_fullTextAttachFlow_v2.isOn &&
            getValues('AGE.locked') &&
            'This field is associated with multiple names.'
          }
          dense
          growX
        />
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights('AGE')}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{
              'data-testid': 'age-highlight-button',
            }}
            onSave={handleHighlightSave('AGE')}
            saveToSls={!isNewPerson && !getValues('AGE.added')} // Only save to SLS if this is not a new person and the field isn't newly added
            growX
            iconOnly
          />
        </Cell>
      </Row>
    </FlowGrid>
  )
}

export default PersonalDetailsForm
